// Deadstock XLSX from existing sku-no-sales response. No API, dependency or writes.
export const DEADSTOCK_HEADERS = [
  "SKU", "Nama Produk", "Qty Dead (stok tersedia)", "HPP/pcs",
  "Nilai Aset", "Penjualan Periode", "Last Sale", "Eksekusi SALE",
];
export const filterDeadstock = (items, onlyAvailable = true) =>
  (Array.isArray(items) ? items : []).filter(item => !onlyAvailable || Number(item.stock_qty ?? 0) > 0);
export function deadstockSheetRows(items) {
  return (Array.isArray(items) ? items : []).map(item => {
    const qty = Number(item.stock_qty ?? 0);
    const value = Number(item.stock_value ?? 0);
    const sold = Number(item.qty_period ?? 0);
    if (![qty, value, sold].every(Number.isFinite)) {
      throw new Error("Angka pada respons Deadstock tidak valid. Export dibatalkan.");
    }
    return [
      String(item.sku_code ?? ""), String(item.description ?? ""), qty,
      qty === 0 ? null : value / qty, // Use unrounded source value and stock before division.
      value, sold, String(item.last_sale_date ?? ""), "",
    ];
  });
}
const encode = text => new TextEncoder().encode(text);
const escapeXml = s => String(s).replace(/&/g, "&amp;")
  .replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
const safeText = s => escapeXml(String(s ?? "").replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g, ""));
const colName = n => {
  let s = "", x = n + 1;
  while (x) { x--; s = String.fromCharCode(65 + x % 26) + s; x = Math.floor(x / 26); }
  return s;
};
const cell = (value, row, col, head) => {
  const ref = colName(col) + row;
  if (typeof value === "number" && Number.isFinite(value)) {
    const style = col === 3 ? 2 : col === 4 ? 3 : 0;
    return `<c r="${ref}" s="${style}" t="n"><v>${value}</v></c>`;
  }
  return `<c r="${ref}"${head ? ' s="1"' : ""} t="inlineStr"><is><t xml:space="preserve">${safeText(value)}</t></is></c>`;
};
const sheet = rows => {
  const data = [DEADSTOCK_HEADERS, ...rows];
  const xmlRows = data.map((row, index) =>
    `<row r="${index + 1}">${row.map((value, c) => cell(value, index + 1, c, index === 0)).join("")}</row>`
  ).join("");
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>` +
    `<sheetFormatPr defaultRowHeight="17"/><cols><col min="1" max="1" width="22" customWidth="1"/>` +
    `<col min="2" max="2" width="42" customWidth="1"/><col min="3" max="3" width="26" customWidth="1"/>` +
    `<col min="4" max="6" width="21" customWidth="1"/><col min="7" max="7" width="18" customWidth="1"/>` +
    `<col min="8" max="8" width="22" customWidth="1"/></cols>` +
    `<sheetData>${xmlRows}</sheetData><autoFilter ref="A1:H${data.length}"/></worksheet>`;
};
const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
  `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
  `<numFmts count="2"><numFmt numFmtId="164" formatCode="#,##0.###############"/>` +
  `<numFmt numFmtId="165" formatCode="&quot;Rp&quot; #,##0.00"/></numFmts>` +
  `<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>` +
  `<font><b/><color rgb="FFFFFFFF"/><sz val="11"/><name val="Calibri"/></font></fonts>` +
  `<fills count="2"><fill><patternFill patternType="none"/></fill>` +
  `<fill><patternFill patternType="solid"><fgColor rgb="FF344563"/><bgColor indexed="64"/></patternFill></fill></fills>` +
  `<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>` +
  `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
  `<cellXfs count="4"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
  `<xf numFmtId="0" fontId="1" fillId="1" borderId="0" xfId="0" applyFont="1" applyFill="1"/>` +
  `<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>` +
  `<xf numFmtId="165" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs>` +
  `</styleSheet>`;
const crcLookup = Array.from({ length: 256 }, (_, n) => {
  let c = n;
  for (let j = 0; j < 8; j++) c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
  return c >>> 0;
});
const crc32 = bytes => {
  let crc = 0xFFFFFFFF;
  for (const b of bytes) crc = crcLookup[(crc ^ b) & 255] ^ (crc >>> 8);
  return (crc ^ 0xFFFFFFFF) >>> 0;
};
const concat = arrays => {
  const out = new Uint8Array(arrays.reduce((n, a) => n + a.length, 0));
  let offset = 0;
  for (const a of arrays) { out.set(a, offset); offset += a.length; }
  return out;
};
// ZIP archive with standard STORE entries, CRC32 and central directory.
function zip(files) {
  const local = [], directory = [];
  let offset = 0;
  for (const [name, xml] of files) {
    const filename = encode(name), bytes = encode(xml), crc = crc32(bytes);
    const lh = new Uint8Array(30 + filename.length), l = new DataView(lh.buffer);
    l.setUint32(0, 0x04034B50, true); l.setUint16(4, 20, true);
    l.setUint16(6, 0x0800, true); l.setUint32(14, crc, true);
    l.setUint32(18, bytes.length, true); l.setUint32(22, bytes.length, true);
    l.setUint16(26, filename.length, true); lh.set(filename, 30);
    local.push(lh, bytes);
    const ch = new Uint8Array(46 + filename.length), c = new DataView(ch.buffer);
    c.setUint32(0, 0x02014B50, true); c.setUint16(4, 20, true);
    c.setUint16(6, 20, true); c.setUint16(8, 0x0800, true);
    c.setUint32(16, crc, true); c.setUint32(20, bytes.length, true);
    c.setUint32(24, bytes.length, true); c.setUint16(28, filename.length, true);
    c.setUint32(42, offset, true); ch.set(filename, 46);
    directory.push(ch); offset += lh.length + bytes.length;
  }
  const body = concat(local), middle = concat(directory), end = new Uint8Array(22);
  const v = new DataView(end.buffer);
  v.setUint32(0, 0x06054B50, true); v.setUint16(8, files.length, true);
  v.setUint16(10, files.length, true);
  v.setUint32(12, middle.length, true); v.setUint32(16, body.length, true);
  return concat([body, middle, end]);
}
export function buildDeadstockXlsx(items, days) {
  if (![7, 14, 30].includes(days)) throw new Error("Periode Deadstock tidak valid.");
  const rows = deadstockSheetRows(items);
  const files = [
    ["[Content_Types].xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
      `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
      `<Default Extension="xml" ContentType="application/xml"/>` +
      `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
      `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
      `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>`],
    ["_rels/.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
      `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
      `</Relationships>`],
    ["xl/workbook.xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
      `<sheets><sheet name="Deadstock ${days} Hari" sheetId="1" r:id="rId1"/></sheets></workbook>`],
    ["xl/_rels/workbook.xml.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
      `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>` +
      `<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
      `</Relationships>`],
    ["xl/worksheets/sheet1.xml", sheet(rows)],
    ["xl/styles.xml", styles],
  ];
  return zip(files);
}
export function downloadDeadstockXlsx(items, days) {
  const data = buildDeadstockXlsx(items, days);
  const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = `deadstock_${days}_hari.xlsx`;
  link.style.display = "none"; document.body.appendChild(link);
  try { link.click(); } finally { link.remove(); URL.revokeObjectURL(url); }
}
